/**
 * Custom fetch mutator for the API client (hand-written today, Orval-ready).
 *
 * - Browser: keeps relative URLs so requests go through the Node reverse proxy
 *   (same-origin, no CORS).
 * - Server (SSR): resolves an absolute backend URL.
 * - Clears auth state and throws a typed AuthExpiredError on 401.
 *
 * @see https://orval.dev/reference/configuration/output#mutator
 */

import { buildConfig, debugLog, isServer } from '@/config';
import { getAuthStore } from '@/stores/authStore';

type ServerRuntimeConfig = { apiPort: number; apiBaseUrl: string };

function getServerRuntimeConfig(): ServerRuntimeConfig | null {
  if (!isServer) return null;
  return (globalThis as any).__SERVER_RUNTIME_CONFIG__ ?? null;
}

export class AuthExpiredError extends Error {
  status = 401;
  wasAuthenticated: boolean;
  constructor(message: string, wasAuthenticated: boolean) {
    super(message);
    this.name = 'AuthExpiredError';
    this.wasAuthenticated = wasAuthenticated;
  }
}

export interface OrvalRequestConfig {
  url: string;
  method: string;
  headers?: Record<string, string>;
  data?: unknown;
  params?: Record<string, unknown>;
  signal?: AbortSignal;
}

function getGenericErrorMessage(status: number): string {
  if (status === 400) return 'Request failed. Please review your input and try again.';
  if (status === 403) return 'You are not allowed to perform this action.';
  if (status === 404) return 'The requested resource could not be found.';
  if (status === 409) return 'This action could not be completed because of a conflict.';
  if (status >= 500) return 'Something went wrong on the server. Please try again.';
  return 'Something went wrong. Please try again.';
}

function extractApiErrorMessage(errorData: unknown, status: number): string {
  if (errorData && typeof errorData === 'object') {
    const record = errorData as Record<string, unknown>;
    const candidate = record.error ?? record.message ?? record.detail;
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  if (typeof errorData === 'string' && errorData.trim()) return errorData.trim();
  return getGenericErrorMessage(status);
}

export const customFetch = async <T>(
  config: OrvalRequestConfig,
  options?: RequestInit,
): Promise<T> => {
  // Prefix the configured base URL. The path already starts with /api/v1.
  let fullUrl = config.url;
  if (isServer) {
    const runtime = getServerRuntimeConfig();
    const apiPort = runtime?.apiPort || buildConfig.apiPort;
    const apiBaseUrl = runtime?.apiBaseUrl || buildConfig.apiBaseUrl;
    if (/^https?:\/\//.test(fullUrl)) {
      // already absolute
    } else if (apiBaseUrl && /^https?:\/\//.test(apiBaseUrl)) {
      fullUrl = new URL(fullUrl, apiBaseUrl).toString();
    } else {
      fullUrl = `http://localhost:${apiPort}${fullUrl}`;
    }
  }

  if (config.params) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(config.params)) {
      if (value !== undefined && value !== null) search.append(key, String(value));
    }
    const qs = search.toString();
    if (qs) fullUrl += `?${qs}`;
  }

  const headers = new Headers(options?.headers ?? {});
  if (config.headers) {
    for (const [key, value] of Object.entries(config.headers)) headers.set(key, value);
  }
  if (!headers.has('Content-Type') && config.data) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  // During SSR there is no browser cookie jar, so forward the request's cookie
  // header (stashed on globalThis per-render) for authenticated prefetches.
  if (isServer) {
    const ssrCookie = (globalThis as any).__SSR_COOKIE__ as string | undefined;
    if (ssrCookie) headers.set('Cookie', ssrCookie);
  }

  debugLog('API Request', { method: config.method, url: fullUrl });

  const response = await fetch(fullUrl, {
    method: config.method,
    headers,
    body: config.data ? JSON.stringify(config.data) : undefined,
    signal: config.signal,
    credentials: 'include',
    ...options,
  });

  if (!response.ok) {
    if (response.status === 401) {
      let wasAuthenticated = false;
      if (!isServer) {
        const store = getAuthStore();
        wasAuthenticated = store.getState().isAuthenticated;
        store.getState().logout();
      }
      throw new AuthExpiredError(
        wasAuthenticated ? 'Session expired' : 'Not authenticated',
        wasAuthenticated,
      );
    }
    const body = await response.text();
    let data: unknown;
    try {
      data = JSON.parse(body);
    } catch {
      data = body;
    }
    const error = new Error(extractApiErrorMessage(data, response.status));
    (error as any).status = response.status;
    (error as any).data = data;
    throw error;
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) return response.json();
  return response.text() as unknown as T;
};

export default customFetch;
